import { useCallback, useReducer, useState } from "react";
import type {
  LaunchableManagedSecretReference,
  LaunchableParameter,
} from "server";
import {
  getLaunchableParameterEffectiveValue,
  hasMissingRequiredParameterBindings,
  launchableParameterSupportsManagedSecret,
} from "../configuration/parameters/LaunchableParameters";

export type ConfigurationSectionKey = "compute" | "ngc-api-key" | "parameters";

interface UseDeployConfigurationOptions {
  initialLocation?: string | null;
  parameters: LaunchableParameter[];
  collectParameters: boolean;
  allowManagedSecrets?: boolean;
}

interface ComputeConfigurationState {
  storageValid: boolean;
  isInstanceOutOfStock: boolean;
  providerDiffers: boolean;
  selectedLocation: string | null;
}

type ComputeConfigurationAction =
  | { type: "set-storage-valid"; value: boolean }
  | { type: "set-instance-out-of-stock"; value: boolean }
  | { type: "set-provider-differs"; value: boolean }
  | { type: "set-selected-location"; value: string | null }
  | { type: "reset-for-instance-change" };

export const computeConfigurationReducer = (
  state: ComputeConfigurationState,
  action: ComputeConfigurationAction,
): ComputeConfigurationState => {
  switch (action.type) {
    case "set-storage-valid":
      if (state.storageValid === action.value) return state;
      return { ...state, storageValid: action.value };
    case "set-instance-out-of-stock":
      if (state.isInstanceOutOfStock === action.value) return state;
      return { ...state, isInstanceOutOfStock: action.value };
    case "set-provider-differs":
      if (state.providerDiffers === action.value) return state;
      return { ...state, providerDiffers: action.value };
    case "set-selected-location":
      if (state.selectedLocation === action.value) return state;
      return { ...state, selectedLocation: action.value };
    case "reset-for-instance-change":
      if (
        state.storageValid &&
        !state.isInstanceOutOfStock &&
        !state.providerDiffers &&
        state.selectedLocation === null
      ) {
        return state;
      }
      return {
        storageValid: true,
        isInstanceOutOfStock: false,
        providerDiffers: false,
        selectedLocation: null,
      };
  }
};

export function useDeployConfiguration({
  initialLocation,
  parameters,
  collectParameters,
  allowManagedSecrets = false,
}: UseDeployConfigurationOptions) {
  const [computeConfiguration, dispatchComputeConfiguration] = useReducer(
    computeConfigurationReducer,
    {
      storageValid: true,
      isInstanceOutOfStock: false,
      providerDiffers: false,
      selectedLocation: initialLocation ?? null,
    },
  );
  const [parameterValues, setParameterValues] = useState<
    Record<string, string>
  >({});
  const [parameterSecretSelections, setParameterSecretSelections] = useState<
    Record<string, LaunchableManagedSecretReference>
  >({});
  const [hasEditedParameters, setHasEditedParameters] = useState(false);
  const [openSection, setOpenSection] =
    useState<ConfigurationSectionKey | null>(null);
  const setStorageValid = useCallback(
    (value: boolean) =>
      dispatchComputeConfiguration({ type: "set-storage-valid", value }),
    [],
  );
  const setIsInstanceOutOfStock = useCallback(
    (value: boolean) =>
      dispatchComputeConfiguration({
        type: "set-instance-out-of-stock",
        value,
      }),
    [],
  );
  const setProviderDiffers = useCallback(
    (value: boolean) =>
      dispatchComputeConfiguration({ type: "set-provider-differs", value }),
    [],
  );
  const setSelectedLocation = useCallback(
    (value: string | null) =>
      dispatchComputeConfiguration({ type: "set-selected-location", value }),
    [],
  );
  const resetForInstanceChange = useCallback(
    () => dispatchComputeConfiguration({ type: "reset-for-instance-change" }),
    [],
  );

  const missingRequiredParameters = hasMissingRequiredParameterBindings(
    collectParameters ? parameters : [],
    parameterValues,
    allowManagedSecrets ? parameterSecretSelections : {},
  );
  const missingRequiredParameterCount = collectParameters
    ? parameters.filter(
        (parameter) =>
          parameter.required &&
          !(
            allowManagedSecrets &&
            launchableParameterSupportsManagedSecret(parameter) &&
            parameterSecretSelections[parameter.name]?.secretId
          ) &&
          !getLaunchableParameterEffectiveValue(parameter, parameterValues),
      ).length
    : 0;

  const toggleSection = (section: ConfigurationSectionKey) => {
    setOpenSection((current) => (current === section ? null : section));
  };

  const updateParameterValues = (values: Record<string, string>) => {
    setParameterValues(values);
    setHasEditedParameters(true);
  };

  const updateParameterSecretSelections = (
    selections: Record<string, LaunchableManagedSecretReference>,
  ) => {
    setParameterSecretSelections(selections);
    setHasEditedParameters(true);
  };

  return {
    storageValid: computeConfiguration.storageValid,
    setStorageValid,
    isInstanceOutOfStock: computeConfiguration.isInstanceOutOfStock,
    setIsInstanceOutOfStock,
    providerDiffers: computeConfiguration.providerDiffers,
    setProviderDiffers,
    parameterValues,
    updateParameterValues,
    parameterSecretSelections,
    updateParameterSecretSelections,
    hasEditedParameters,
    selectedLocation: computeConfiguration.selectedLocation,
    setSelectedLocation,
    resetForInstanceChange,
    openSection,
    toggleSection,
    missingRequiredParameters,
    missingRequiredParameterCount,
  };
}
